<script setup lang="ts">
import { Clock } from 'lucide-vue-next'
import DomainCard from '@/views/domain/components/DomainCard.vue'
import SenderConfig from '@/views/domain/components/SenderConfig.vue'

import Forbidden from '@/components/errors/403.vue'

import type { TabKey } from '@/components/base/BaseTab.vue'
import type { Icons } from '@/components/base/BaseIcon.vue'
import type { DnsDiagnostic, EmailDomain } from '@/composables/domainConfig'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { domains, getDnsDiagnostic, listDomains, sendTestEmail, verifyDomain, deleteDomain, isLoadingDomain } =
  useDomainConfig()
const { isDirection } = useAccess()
const { user } = useLocalStorage()

const currentTab = ref((route?.query?.activeTab as string) || 'domain')
const isInitialLoading = ref(true)

const tabs = computed<TabKey[]>(() => [
  {
    value: 'domain',
    name: t('pages.domain.tabs.domain')
  },
  {
    value: 'sender',
    name: t('pages.domain.tabs.sender')
  }
])

const pageTitle = computed(() => ({
  title: t('pages.domain.title')
}))

const crumbs = computed(() => [
  {
    name: t('pages.domain.title')
  }
])

const diagnostics = ref<Record<number, DnsDiagnostic | null>>({})

const loadDiagnostics = async () => {
  const pending = domains.value.filter((domain) => !domain.is_ready_for_sending)
  const results = await Promise.all(pending.map((domain) => getDnsDiagnostic(domain.id)))
  pending.forEach((domain, index) => {
    diagnostics.value[domain.id] = results[index]
  })
}

const hasDomains = computed(() => domains.value.length > 0)
const hasPendingDomain = computed(() => domains.value.some((domain) => !domain.is_ready_for_sending))
const showPropagationTip = computed(() =>
  domains.value.some(
    (domain) => !domain.is_ready_for_sending && diagnostics.value[domain.id]?.summary === 'propagating'
  )
)
const sendingDomain = computed(() => domains.value.find((domain) => domain.is_ready_for_sending) ?? null)

const handleAddDomain = () => {
  router.push({ name: 'DomainConfig' })
}

const handleViewDnsConfig = (domain: EmailDomain) => {
  router.push({ name: 'DomainConfig', query: { domainId: domain.id } })
}

const handleVerifyDomain = async (domain: EmailDomain) => {
  await verifyDomain(domain.id)
  const updated = await listDomains()

  if (!updated.find((d) => d.id === domain.id)?.is_ready_for_sending) {
    handleViewDnsConfig(domain)
  }
}

const domainToDelete = ref<EmailDomain | null>(null)

const handleAskDelete = (domain: EmailDomain) => {
  domainToDelete.value = domain
}

const handleConfirmDelete = async () => {
  const domain = domainToDelete.value
  if (!domain) return

  const isDeleted = await deleteDomain(domain.id)
  domainToDelete.value = null

  if (isDeleted) await listDomains()
}

const isTestEmailModalOpen = ref(false)
const testEmailAddress = ref('')
const isSendingTestEmail = ref(false)

const handleOpenTestEmailModal = () => {
  testEmailAddress.value = user.value.email || ''
  isTestEmailModalOpen.value = true
}

const handleSendTestEmail = async () => {
  if (!testEmailAddress.value) return

  isSendingTestEmail.value = true
  const result = await sendTestEmail(testEmailAddress.value)
  isSendingTestEmail.value = false

  if (result.success) {
    isTestEmailModalOpen.value = false
    return
  }

  isTestEmailModalOpen.value = false

  if (result.error === 'email_settings_not_configured') {
    currentTab.value = 'sender'
  } else if (result.error === 'sender_domain_not_verified') {
    const target = sendingDomain.value ?? domains.value[0]
    if (target) handleViewDnsConfig(target)
  }
}

const features = computed<{ icon: Icons; title: string; description: string }[]>(() => [
  {
    icon: 'IconoirDoubleCheck',
    title: t('pages.domain.emptyState.features.credibility.title'),
    description: t('pages.domain.emptyState.features.credibility.description')
  },
  {
    icon: 'IconoirHistoricShield',
    title: t('pages.domain.emptyState.features.deliverability.title'),
    description: t('pages.domain.emptyState.features.deliverability.description')
  },
  {
    icon: 'IconoirArrowRight',
    title: t('pages.domain.emptyState.features.guided.title'),
    description: t('pages.domain.emptyState.features.guided.description')
  }
])

onMounted(async () => {
  await listDomains()
  isInitialLoading.value = false
  loadDiagnostics()
})
</script>

<template>
  <Forbidden v-if="!isDirection" :title="pageTitle.title" :crumbs="crumbs" />

  <div v-else>
    <TheListHeader :title="pageTitle.title" />

    <div v-if="hasDomains" class="mb-4 px-6 sm:px-12">
      <BaseTab v-model="currentTab" :tabs="tabs" />
    </div>

    <div class="px-6 sm:px-12 pb-6 sm:pb-12">
      <div v-if="currentTab === 'domain'">
        <div v-if="isInitialLoading" class="w-full">
          <Card>
            <CardContent class="flex flex-col gap-4">
              <div class="flex flex-col gap-2">
                <AppSkeleton type="text" class="h-6 w-48" />
                <AppSkeleton type="text" class="h-4 w-72" />
              </div>

              <div class="border border-border rounded-lg p-4">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-4">
                    <AppSkeleton type="section" class="!w-10 !h-10 rounded-md" section-height="h-10" />
                    <div class="flex flex-col gap-2">
                      <div class="flex items-center gap-2">
                        <AppSkeleton type="text" class="h-4 w-32" />
                        <AppSkeleton type="text" class="h-5 w-16 rounded-full" />
                      </div>
                      <AppSkeleton type="text" class="h-3 w-56" />
                    </div>
                  </div>
                  <AppSkeleton type="button" />
                </div>
              </div>

              <div class="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                <AppSkeleton type="section" class="!w-5 !h-5 !bg-blue-200" section-height="h-5" />
                <div class="flex flex-col gap-2 flex-1">
                  <AppSkeleton type="text" class="h-4 w-40 !bg-blue-200" />
                  <AppSkeleton type="text" class="h-3 w-full !bg-blue-100" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div v-else-if="hasDomains" class="w-full">
          <Card>
            <CardContent class="flex flex-col gap-4">
              <div class="flex flex-col gap-6">
                <div>
                  <h2 class="text-lg font-semibold text-foreground">
                    {{ t('pages.domain.domainCard.title') }}
                  </h2>
                  <p class="text-sm text-muted-foreground mt-1">
                    {{ t('pages.domain.domainCard.description') }}
                  </p>
                </div>

                <div class="flex flex-col gap-3">
                  <DomainCard
                    v-for="domain in domains"
                    :key="domain.id"
                    :domain="domain"
                    :is-loading="isLoadingDomain"
                    :diagnostic="diagnostics[domain.id] ?? null"
                    @verify="handleVerifyDomain"
                    @view-dns="handleViewDnsConfig"
                    @test-email="handleOpenTestEmailModal"
                    @delete="handleAskDelete"
                  />
                </div>
              </div>

              <BaseTip v-if="hasPendingDomain && showPropagationTip" severity="info" class="custom_message">
                <template #icon>
                  <Clock />
                </template>
                <template #text>
                  <div class="flex flex-col gap-2">
                    <span class="font-medium">{{ t('pages.domain.domainCard.propagationTitle') }}</span>
                    <span>{{ t('pages.domain.domainCard.propagationDescription') }}</span>
                  </div>
                </template>
              </BaseTip>
            </CardContent>
          </Card>
        </div>

        <Card v-else>
          <CardContent class="flex flex-col items-center justify-center py-10">
            <div class="max-w-2xl text-center">
              <div class="w-16 h-16 mx-auto mb-6 bg-primary/5 rounded-full flex items-center justify-center">
                <BaseIcon name="IconoirMail" class="w-8 h-8 text-primary" />
              </div>

              <h2 class="text-xl font-semibold text-foreground mb-2">
                {{ t('pages.domain.emptyState.title') }}
              </h2>

              <p class="text-muted-foreground mb-8">
                {{ t('pages.domain.emptyState.description') }}
              </p>

              <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <Card v-for="feature in features" :key="feature.title" class="!shadow-none border border-border">
                  <CardHeader>
                    <CardTitle>
                      <div class="text-left flex flex-col gap-6">
                        <BaseIcon :name="feature.icon" class="w-5 h-5 text-primary" />
                        <h3 class="text-sm font-medium text-foreground mb-1">
                          {{ feature.title }}
                        </h3>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p class="text-xs text-muted-foreground text-left">
                      {{ feature.description }}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Button variant="default-primary" @click="handleAddDomain">
                {{ t('pages.domain.emptyState.button') }}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div v-if="currentTab === 'sender'">
        <SenderConfig :domain="sendingDomain?.domain || null" />
      </div>
    </div>

    <BaseModal
      :is-open="isTestEmailModalOpen"
      :text-title="t('pages.domain.testEmail.modal.title')"
      :text="t('pages.domain.testEmail.modal.description')"
      :cancel="t('pages.domain.testEmail.modal.cancel')"
      :link-text="t('pages.domain.testEmail.modal.send')"
      :is-processing="isSendingTestEmail"
      :is-disabled="!testEmailAddress"
      size="sm"
      @close="isTestEmailModalOpen = false"
      @click="handleSendTestEmail"
    >
      <AppFormInput
        id="testEmailAddress"
        v-model="testEmailAddress"
        :label="t('pages.domain.testEmail.modal.label')"
        type="email"
        placeholder="exemple@email.com"
        name="testEmailAddress"
        class="mt-4"
        disabled
      />
    </BaseModal>

    <BaseModal
      :is-open="domainToDelete !== null"
      :text-title="t('pages.domain.deleteModal.title')"
      :text="t('pages.domain.deleteModal.description', { domain: domainToDelete?.domain })"
      :cancel="t('pages.domain.config.buttons.cancel')"
      :link-text="t('pages.domain.deleteModal.confirm')"
      :is-processing="isLoadingDomain"
      icon="IconoirWarningTriangle"
      icon-color="orange"
      size="lg"
      @close="domainToDelete = null"
      @click="handleConfirmDelete"
    />
  </div>
</template>
